import { useAgentChat } from "@cloudflare/ai-chat/react";
import { useAgent } from "agents/react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useBackgroundTasks } from "@/hooks/useBackgroundTasks";
import { ChatInput } from "./ChatInput";
import { MessageBubble } from "./MessageBubble";
import { StreamingIndicator } from "./StreamingIndicator";
import { TaskStatusBar } from "./TaskStatusBar";

interface ChatProps {
  userId: string;
  conversationId: string;
  onNewChat: () => void;
  onMessageSent?: () => void;
}

export function Chat({
  userId,
  conversationId,
  onNewChat,
  onMessageSent,
}: ChatProps) {
  const agent = useAgent({
    agent: "deft-agent",
    name: `${userId}:${conversationId}`,
    host: import.meta.env.VITE_API_URL || undefined,
  });

  const { messages, sendMessage, status } = useAgentChat({ agent });
  const { activeTasks, tasks } = useBackgroundTasks(conversationId);

  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isLoading = status === "streaming" || status === "submitted";

  const messageCount = messages.length;
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messageCount]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;
    sendMessage({ text: trimmed });
    setInput("");
    if (onMessageSent) {
      setTimeout(onMessageSent, 2000);
    }
  };

  const handleNewChat = () => {
    onNewChat();
  };

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
        {messages.length === 0 && (
          <div className="flex h-full items-center justify-center text-gray-400">
            Send a message to get started
          </div>
        )}
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
        {isLoading && <StreamingIndicator />}
        <div ref={messagesEndRef} />
      </div>
      <TaskStatusBar activeTasks={activeTasks} tasks={tasks} />
      <ChatInput
        input={input}
        onInputChange={setInput}
        onSubmit={handleSubmit}
        onNewChat={handleNewChat}
        isLoading={isLoading}
      />
    </div>
  );
}
